const stopWordsForHighlight = [
  'the',
  'a',
  'in',
  'is',
  'to',
  'of',
  'for',
  'and',
  'or',
  'but',
  'them',
  'this',
  'that',
  'an',
  'with',
];

export const getBaseSizeProps = (
  displayLevel?: number | 'default',
  inline?: boolean,
): {
  titleSize: 'sm' | 'md' | 'lg' | 'xl';
  bodySize: 'sm' | 'md' | 'lg' | 'inherit';
  buttonSize: 'sm' | 'md' | 'lg';
} => {
  if (displayLevel === 1) {
    return {
      titleSize: 'xl',
      bodySize: inline ? 'inherit' : 'md',
      buttonSize: 'lg',
    };
  }
  if (displayLevel === 2) {
    return {
      titleSize: 'lg',
      bodySize: inline ? 'inherit' : 'md',
      buttonSize: 'lg',
    };
  }
  if (displayLevel === 4) {
    return {
      titleSize: 'sm',
      bodySize: inline ? 'inherit' : 'sm',
      buttonSize: 'md',
    };
  }
  if (displayLevel === 5) {
    return {
      titleSize: 'sm',
      bodySize: inline ? 'inherit' : 'sm',
      buttonSize: 'sm',
    };
  }
  return {
    titleSize: 'md',
    bodySize: inline ? 'inherit' : 'sm',
    buttonSize: 'md',
  };
};

/**
 * Normalizes problematic Unicode characters commonly found in Contentful content.
 * This fixes Safari-specific rendering issues with invisible Unicode characters.
 *
 * Handles:
 * - U+2019 (') - Right single quotation mark → ASCII apostrophe (')
 * - U+2018 (') - Left single quotation mark → ASCII apostrophe (')
 * - U+201C (") - Left double quotation mark → ASCII quote (")
 * - U+201D (") - Right double quotation mark → ASCII quote (")
 * - U+2028 - Line separator → Normal space
 * - U+2029 - Paragraph separator → Normal space
 *
 * @param text - The text to normalize
 * @returns The normalized text with standard ASCII characters
 */
export const normalizeContentfulText = (text?: string | null): string => {
  if (!text) {
    return '';
  }

  return text
    .replace(/[\u2018\u2019]/g, "'") // Curly single quotes → straight apostrophe
    .replace(/[\u201C\u201D]/g, '"') // Curly double quotes → straight quote
    .replace(/[\u2028\u2029]/g, ' ') // Line/paragraph separators → space
    .replace(/\u00A0/g, ' '); // Non-breaking space → regular space
};

export const getHighlightedText = (
  text: string | undefined | null,
  highlight: string,
) => {
  if (!text) {
    return null;
  }
  const words = highlight
    .trim()
    .split(/\s+/)
    .filter((word) => word.length >= 3 && !stopWordsForHighlight.includes(word))
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  if (words.length === 0) {
    return <span>{text}</span>;
  }

  const pattern = new RegExp(`\\b(${words.join('|')})\\b`, 'gi');
  const parts = text.split(pattern);

  return (
    <span>
      {parts.map((part, i) => (
        <span
          key={i}
          className={
            words.some((w) => new RegExp(`^${w}$`, 'i').test(part.trim()))
              ? 'SearchResults--highlight'
              : ''
          }
        >
          {part}
        </span>
      ))}
    </span>
  );
};
